import classnames from 'classnames';
import { COMPENDIUM_CHAPTER_NAME_MAX_LENGTH, type Vector2 } from '@sorrem/shared';
import type { UICompendium, UICompendiumChapter } from '@sorrem-client/types';
import { Icon } from '@sorrem-client/ui/components';
import { useCompendium, useConfirm, useDragSource, useDropTarget, type DragSourceOptions, type DropTargetOptions } from '@sorrem-client/ui/hooks';
import { useEffect, useRef, useState, type HTMLAttributes } from 'react';
import {
    findCompendiumPageNumber,
    toEssenceEmblemUrl,
    findCompendiumVisionPlace,
    type CompendiumSelection,
} from '../../compendium-pages';
import { PageHeading, PageNote } from '../FieldJournal';
import styles from './CompendiumBook.module.scss';

const NEW_CHAPTER_NAME = 'New Chapter';

/** What is being dragged in the contents. */
type DragItem = { kind: 'vision'; visionId: string } | { kind: 'chapter'; chapterId: string };

/**
 * Where a drop would land: a vision before `index` in a chapter (the loose
 * pages included), or a chapter before `index` among the player's chapters.
 * Indexes count the dragged item where it is now.
 */
type DropTarget = { kind: 'vision'; chapterId: string; index: number } | { kind: 'chapter'; index: number };

interface CompendiumContentsProps {
    compendium: UICompendium;
    selection: CompendiumSelection | null;
    onSelect: (selection: CompendiumSelection) => void;
}

/**
 * The Compendium's contents: each chapter with its pages, the loose pages
 * last. The player drags pages between chapters and reorders them, drags
 * chapters to reorder them, and names, adds and removes chapters here. The
 * loose pages are a chapter too, but stay last and can't be renamed or removed.
 */
export function CompendiumContents({ compendium, selection, onSelect }: CompendiumContentsProps): JSX.Element {
    // Each useCompendium() keeps its own error, so it is shown from the one that makes the changes.
    const { error, createChapter, renameChapter, deleteChapter, moveChapter, setChapterVisions } = useCompendium();
    const { confirm } = useConfirm();
    const [dragging, setDragging] = useState<DragItem | null>(null);
    const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
    const [renamingId, setRenamingId] = useState<string | null>(null);

    const ownChapters = compendium.chapters.filter((chapter) => !chapter.isLoose);
    const isEmpty = ownChapters.length === 0 && compendium.chapters.every((chapter) => chapter.visions.length === 0);

    const endDrag = (): void => {
        setDragging(null);
        setDropTarget(null);
    };

    /** Lets a drop happen here, and marks where it would land. False refuses it. */
    const allowDrop = (target: DropTarget): boolean => {
        if (dragging === null || dragging.kind !== target.kind) {
            return false;
        }
        setDropTarget(target);
        return true;
    };

    const handleDrop = (): void => {
        const item = dragging;
        const target = dropTarget;
        endDrag();
        if (item === null || target === null) {
            return;
        }
        if (item.kind === 'vision' && target.kind === 'vision') {
            const from = findCompendiumVisionPlace(compendium, item.visionId);
            const chapter = compendium.chapters.find((entry) => entry.id === target.chapterId);
            if (from === undefined || chapter === undefined) {
                return;
            }
            // The chapter's pages with this one placed where it was dropped. Taken
            // out of its own list first, a move further down lands one earlier.
            const isSameChapter = from.chapter.id === chapter.id;
            const index = isSameChapter && from.index < target.index ? target.index - 1 : target.index;
            if (isSameChapter && from.index === index) {
                return;
            }
            const others = chapter.visions.map((vision) => vision.visionId).filter((visionId) => visionId !== item.visionId);
            void setChapterVisions(chapter.id, [...others.slice(0, index), item.visionId, ...others.slice(index)]);
        } else if (item.kind === 'chapter' && target.kind === 'chapter') {
            const from = ownChapters.findIndex((chapter) => chapter.id === item.chapterId);
            const index = from < target.index ? target.index - 1 : target.index;
            if (from !== index) {
                void moveChapter(item.chapterId, index);
            }
        }
    };

    /** Before or after a row, by which half of it the pointer is over. */
    const isLowerHalf = (point: Vector2, rect: DOMRect): boolean => point.y > rect.top + rect.height / 2;

    const handleNewChapter = async (): Promise<void> => {
        const chapterId = await createChapter(NEW_CHAPTER_NAME);
        if (chapterId !== null) {
            onSelect({ kind: 'chapter', chapterId });
            setRenamingId(chapterId);
        }
    };

    const handleRemoveChapter = async (chapterId: string, name: string): Promise<void> => {
        const isConfirmed = await confirm({
            title: `Remove "${name}"?`,
            description: 'Its pages go back to the loose pages; no vision is lost.',
            confirmLabel: 'Remove Chapter',
        });
        if (isConfirmed) {
            void deleteChapter(chapterId);
        }
    };

    const buildVisionRows = (chapter: UICompendiumChapter): JSX.Element[] => chapter.visions.map((vision, index) => {
        const isSelected = selection?.kind === 'vision' && selection.visionId === vision.visionId;
        const isDropRow = dropTarget?.kind === 'vision' && dropTarget.chapterId === chapter.id;
        const isDropBefore = isDropRow && dropTarget.index === index;
        const isDropAfter = isDropRow && dropTarget.index === index + 1 && index === chapter.visions.length - 1;
        return (
            <DragRow
                as="li"
                key={vision.visionId}
                className={classnames(
                    styles.pageRow,
                    isSelected && styles.selected,
                    isDropBefore && styles.dropBefore,
                    isDropAfter && styles.dropAfter,
                    dragging?.kind === 'vision' && dragging.visionId === vision.visionId && styles.dragging,
                )}
                drag={{ cursor: 'move', onStart: () => setDragging({ kind: 'vision', visionId: vision.visionId }), onEnd: endDrag }}
                drop={{
                    onOver: (point, rect) => allowDrop({ kind: 'vision', chapterId: chapter.id, index: isLowerHalf(point, rect) ? index + 1 : index }),
                    onDrop: handleDrop,
                }}
            >
                <span className={styles.grip} aria-hidden="true"><Icon icon="grip" size="small" /></span>
                <button
                    type="button"
                    className={styles.pageButton}
                    aria-pressed={isSelected}
                    onClick={() => onSelect({ kind: 'vision', visionId: vision.visionId })}
                >
                    <img className={styles.emblem} src={toEssenceEmblemUrl(vision.essence)} alt="" draggable={false} />
                    <span className={styles.pageTitle}>{vision.title}</span>
                    <span className={styles.pageNumber}>Page {findCompendiumPageNumber(compendium, vision.visionId)}</span>
                </button>
            </DragRow>
        );
    });

    return (
        <nav className={styles.contents} aria-label="Compendium contents">
            <PageHeading title="Contents" />

            {isEmpty && <PageNote>Your Compendium is empty.</PageNote>}

            {/* An empty Compendium shows no headings at all, not even the loose pages. */}
            {!isEmpty && compendium.chapters.map((chapter) => {
                const chapterIndex = ownChapters.indexOf(chapter);
                const isSelected = selection?.kind === 'chapter' && selection.chapterId === chapter.id;
                const isRenaming = renamingId === chapter.id;
                return (
                    <section key={chapter.id} className={styles.chapter}>
                        <DragRow
                            as="div"
                            className={classnames(
                                styles.chapterHeading,
                                isSelected && styles.selected,
                                !chapter.isLoose && dropTarget?.kind === 'chapter' && dropTarget.index === chapterIndex && styles.dropBefore,
                                !chapter.isLoose && dropTarget?.kind === 'chapter' && dropTarget.index === chapterIndex + 1
                                    && chapterIndex === ownChapters.length - 1 && styles.dropAfter,
                                dropTarget?.kind === 'vision' && dropTarget.chapterId === chapter.id && chapter.visions.length === 0 && styles.dropInto,
                                dragging?.kind === 'chapter' && dragging.chapterId === chapter.id && styles.dragging,
                            )}
                            drag={chapter.isLoose || isRenaming
                                ? undefined
                                : { cursor: 'move', onStart: () => setDragging({ kind: 'chapter', chapterId: chapter.id }), onEnd: endDrag }}
                            drop={{
                                onOver: (point, rect) => {
                                    if (dragging?.kind === 'vision') {
                                        // A page dropped on a heading goes to the end of that chapter.
                                        return allowDrop({ kind: 'vision', chapterId: chapter.id, index: chapter.visions.length });
                                    }
                                    // Chapters reorder among themselves; the loose pages stay last.
                                    return !chapter.isLoose && allowDrop({ kind: 'chapter', index: isLowerHalf(point, rect) ? chapterIndex + 1 : chapterIndex });
                                },
                                onDrop: handleDrop,
                            }}
                        >
                            {!chapter.isLoose && <span className={styles.grip} aria-hidden="true"><Icon icon="grip" size="small" /></span>}
                            {isRenaming ? (
                                <ChapterNameInput
                                    initialName={chapter.name}
                                    onDone={(name) => {
                                        setRenamingId(null);
                                        if (name !== null && name !== chapter.name) {
                                            void renameChapter(chapter.id, name);
                                        }
                                    }}
                                />
                            ) : (
                                <button
                                    type="button"
                                    className={styles.chapterName}
                                    aria-pressed={isSelected}
                                    onClick={() => onSelect({ kind: 'chapter', chapterId: chapter.id })}
                                >
                                    {chapter.name}
                                </button>
                            )}
                            {!chapter.isLoose && (
                                <>
                                    <button
                                        type="button"
                                        className={styles.chapterTool}
                                        aria-label={`Rename ${chapter.name}`}
                                        onClick={() => setRenamingId(chapter.id)}
                                    >
                                        <Icon icon="pencil" size="small" />
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.chapterTool}
                                        aria-label={`Remove ${chapter.name}`}
                                        onClick={() => void handleRemoveChapter(chapter.id, chapter.name)}
                                    >
                                        <Icon icon="decline" size="small" />
                                    </button>
                                </>
                            )}
                        </DragRow>
                        <ol className={styles.pageList}>
                            {buildVisionRows(chapter)}
                        </ol>
                    </section>
                );
            })}

            <button type="button" className={styles.newChapter} onClick={() => void handleNewChapter()}>
                <Icon icon="plus" size="small" /> New Chapter
            </button>
            {error !== null && <p className={styles.error} role="alert">{error}</p>}
        </nav>
    );
}

interface DragRowProps extends HTMLAttributes<HTMLElement> {
    readonly as: 'li' | 'div';
    /** Makes the row draggable. */
    readonly drag?: DragSourceOptions;
    /** Makes the row a place to drop. */
    readonly drop?: DropTargetOptions;
}

/** A contents row (a page or a chapter heading) that can be dragged and dropped on. */
function DragRow({ as: Tag, drag, drop, ...props }: DragRowProps): JSX.Element {
    const dragSource = useDragSource(drag);
    const dropTarget = useDropTarget(drop);
    return <Tag {...props} {...dragSource} {...dropTarget} />;
}

/** Edits a chapter's name in place: Enter or leaving the field keeps it, Escape cancels (null). */
export function ChapterNameInput({ initialName, onDone }: { initialName: string; onDone: (name: string | null) => void }): JSX.Element {
    const [name, setName] = useState(initialName);
    const inputRef = useRef<HTMLInputElement>(null);
    // Done once: the blur as the field closes after Enter or Escape must neither save again nor undo a cancel (#330).
    const isDoneRef = useRef(false);
    const done = (result: string | null): void => {
        if (isDoneRef.current) return;
        isDoneRef.current = true;
        onDone(result);
    };
    const finish = (): void => done(name.trim() === '' ? null : name.trim());

    // Focused and selected on opening, so the player can type a name at once.
    useEffect(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
    }, []);

    return (
        <input
            className={styles.chapterNameInput}
            value={name}
            maxLength={COMPENDIUM_CHAPTER_NAME_MAX_LENGTH}
            aria-label="Chapter name"
            ref={inputRef}
            onChange={(event) => setName(event.target.value)}
            onBlur={finish}
            onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    finish();
                } else if (event.key === 'Escape') {
                    event.preventDefault();
                    done(null);
                }
            }}
        />
    );
}
